/**
 * Switch Component
 * Toggle switch control
 */

import React, { forwardRef, useState } from 'react';
import { Box } from '../primitives/Box';
import { useTheme } from '../theme';

/**
 * Switch Component
 * 
 * @example
 * <Switch isChecked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
 */
export const Switch = forwardRef(({
  isChecked,
  defaultChecked = false,
  onChange,
  isDisabled = false,
  isReadOnly = false,
  isInvalid = false,
  isRequired = false,
  size = 'md',
  colorScheme = 'brand',
  id,
  name,
  value,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  ...props
}, ref) => {
  const theme = useTheme();
  const switchStyles = theme?.components?.Switch || {};
  
  // Controlled vs uncontrolled
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const checked = isChecked !== undefined ? isChecked : internalChecked;

  const handleChange = (e) => {
    if (isDisabled || isReadOnly) return;
    if (isChecked === undefined) {
      setInternalChecked(e.target.checked);
    }
    onChange?.(e);
  };

  // Size styles
  const sizeStyles = {
    sm: { track: { w: '1.375rem', h: '0.75rem' }, thumb: { w: '0.5rem', h: '0.5rem', transform: checked ? 'translateX(0.625rem)' : 'translateX(0)' } },
    md: { track: { w: '1.875rem', h: '1rem' }, thumb: { w: '0.75rem', h: '0.75rem', transform: checked ? 'translateX(0.875rem)' : 'translateX(0)' } },
    lg: { track: { w: '2.875rem', h: '1.5rem' }, thumb: { w: '1.25rem', h: '1.25rem', transform: checked ? 'translateX(1.375rem)' : 'translateX(0)' } },
  };

  const currentSize = sizeStyles[size] || sizeStyles.md;

  return (
    <Box
      as="label"
      display="inline-flex"
      alignItems="center"
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.4 : 1}
      {...props}
    >
      <input
        ref={ref}
        type="checkbox"
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={handleChange}
        disabled={isDisabled}
        readOnly={isReadOnly}
        required={isRequired}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-invalid={isInvalid}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          border: 0,
        }}
      />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        boxSizing="border-box"
        borderRadius="full"
        p="2px"
        transition="background-color 0.25s"
        bg={checked ? `${colorScheme}.500` : 'gray.200'}
        {...currentSize.track}
      >
        <Box
          borderRadius="50%"
          bg="white"
          transition="transform 0.25s"
          boxShadow="sm"
          {...currentSize.thumb}
        />
      </Box>
    </Box>
  );
});

Switch.displayName = 'Switch';

export default Switch;
